/**
 * @file Web-side cryptography helpers for the export flow.
 *
 * Three levels of unwrap/decrypt:
 *
 *   1. transport_key (URL fragment, 32 bytes)
 *      ⇣ AES-256-GCM decrypt with iv from Firestore
 *      encrypted_kek (Firestore) → plain KEK bytes (32 bytes)
 *
 *   2. plain KEK bytes
 *      ⇣ crypto.subtle.importKey(..., extractable: false, ['decrypt'])
 *      KEK CryptoKey (non-extractable)
 *      ⇣ kekBytes.fill(0)  // export_security_v1 §3.5 対策 D
 *
 *   3. wrapped_dek (Firestore record)
 *      ⇣ crypto.subtle.decrypt with KEK CryptoKey
 *      DEK plain bytes (32 bytes)
 *      ⇣ importKey for AES-GCM data decrypt
 *      DEK CryptoKey
 *      ⇣ used to decrypt text fields and media bytes
 *      ⇣ dekBytes.fill(0) right after import
 *
 * All envelopes use the layout: `nonce(12) || ciphertext || tag(16)` to match
 * `app/lib/infrastructure/security/aes_gcm_envelope.dart`. WebCrypto's
 * AES-GCM API returns `ciphertext || tag` from `encrypt()` and accepts the
 * same layout in `decrypt()`, so we slice off the leading nonce explicitly.
 */

import { decodeBase64 } from "./url";

/**
 * Copy a Uint8Array into a fresh ArrayBuffer-backed Uint8Array. Required
 * because TS 5.7's DOM types reject `Uint8Array<ArrayBufferLike>` (which
 * could be SharedArrayBuffer-backed) as a `BufferSource` for crypto.subtle.
 */
function toFreshBuffer(src: Uint8Array): Uint8Array<ArrayBuffer> {
  const ab = new ArrayBuffer(src.byteLength);
  const out = new Uint8Array(ab);
  out.set(src);
  return out;
}

const NONCE_LENGTH = 12;
const TAG_LENGTH_BYTES = 16;
const KEY_LENGTH_BYTES = 32;

export class CryptoError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "CryptoError";
  }
}

/** Best-effort zero-fill for a Uint8Array. */
export function zeroFill(buf: Uint8Array): void {
  buf.fill(0);
}

/**
 * @what: Decrypt the Firestore `encrypted_kek` (= ciphertext || tag, base64)
 *   using the `transport_key` carried in the URL fragment, and return the
 *   plain 32-byte KEK bytes.
 * @why: モバイル側で family_kek を `transport_key` でラップして Firestore に
 *   書き込んだ `encrypted_kek` / `iv` を受け取る側 (Web) で復号する。
 *   `transport_key` は QR でしか共有されないため、Firestore + GCS の単独侵害では
 *   復号不能となる (export_security_v1 §3.5 対策 D の前段)。
 * @failure: `CryptoError` を throw する条件:
 *   - transport_key の長さが 32 byte 以外
 *   - iv の長さが 12 byte 以外
 *   - encrypted_kek が tag (16 byte) より短い
 *   - AES-GCM の auth tag が一致しない (transport_key 不一致 or 改ざん)
 *   - 復号後の長さが 32 byte 以外 (想定外の payload)
 *   いずれの場合も、呼び出し側 (`useExportSession`) が catch して
 *   `decryption_error` カテゴリで Firestore session を `failed` に倒す。
 */
export async function decryptEncryptedKek(args: {
  transportKey: Uint8Array; // 32 bytes
  encryptedKekBase64: string; // base64(ciphertext || tag)
  ivBase64: string; // base64(nonce, 12 bytes)
}): Promise<Uint8Array> {
  if (args.transportKey.byteLength !== KEY_LENGTH_BYTES) {
    throw new CryptoError(
      `transport_key must be ${KEY_LENGTH_BYTES} bytes, got ${args.transportKey.byteLength}`,
    );
  }
  const transportKeyForApi = toFreshBuffer(args.transportKey);
  const transportCryptoKey = await crypto.subtle.importKey(
    "raw",
    transportKeyForApi,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );
  zeroFill(transportKeyForApi);

  const ctAndTag = decodeBase64(args.encryptedKekBase64);
  if (ctAndTag.byteLength < TAG_LENGTH_BYTES) {
    throw new CryptoError("encrypted_kek shorter than tag length");
  }
  const iv = decodeBase64(args.ivBase64);
  if (iv.byteLength !== NONCE_LENGTH) {
    throw new CryptoError(`iv must be ${NONCE_LENGTH} bytes, got ${iv.byteLength}`);
  }

  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toFreshBuffer(iv) },
      transportCryptoKey,
      toFreshBuffer(ctAndTag),
    );
  } catch {
    throw new CryptoError("KEK decrypt failed (transport_key mismatch or tampered ciphertext)");
  }
  if (plaintext.byteLength !== KEY_LENGTH_BYTES) {
    throw new CryptoError(`unexpected KEK length: ${plaintext.byteLength}`);
  }
  return new Uint8Array(plaintext);
}

/**
 * @what: Import the plain KEK bytes into WebCrypto with `extractable: false`
 *   so the returned CryptoKey can decrypt but its bytes cannot be exported
 *   back to JavaScript.
 * @why: 悪意ある拡張機能や XSS が `crypto.subtle.exportKey(kek)` を呼んでも
 *   `InvalidAccessError` で失敗するようにする
 *   (export_security_v1 §3.5 対策 D)。呼び出し側は import 完了後に
 *   入力 `kekBytes` を `zeroFill` で破棄する責務を持つ (本関数は副作用なし)。
 * @failure: `CryptoError` を throw する条件:
 *   - kekBytes の長さが 32 byte 以外
 *   - WebCrypto の `importKey` 内部失敗 (algorithm support 等; 主要ブラウザは
 *     AES-GCM をサポートするため通常起こらない)。
 */
export async function importKekNonExtractable(kekBytes: Uint8Array): Promise<CryptoKey> {
  if (kekBytes.byteLength !== KEY_LENGTH_BYTES) {
    throw new CryptoError(`KEK must be ${KEY_LENGTH_BYTES} bytes, got ${kekBytes.byteLength}`);
  }
  return crypto.subtle.importKey(
    "raw",
    toFreshBuffer(kekBytes),
    { name: "AES-GCM" },
    false, // extractable: false (export_security_v1 §3.5 対策 D)
    ["decrypt"],
  );
}

/**
 * @what: Unwrap a wrapped_dek (`base64(nonce || ciphertext || tag)`, written
 *   by `AesGcmEnvelope.wrap` on the Dart side) and return the 32-byte DEK
 *   plain bytes.
 * @why: 各 Entry / Observation / Digest は固有の DEK を持ち、その DEK が KEK で
 *   ラップされて Firestore に保存されている。Web 側はここで KEK CryptoKey で
 *   DEK を復号して、テキストとメディアの復号に使う。
 * @failure: `CryptoError` を throw する条件:
 *   - envelope の長さが nonce(12) + tag(16) より短い (= malformed payload)
 *   - AES-GCM の auth tag が一致しない (KEK mismatch / 改ざん)
 *   - 復号後の長さが 32 byte 以外
 *   呼び出し側はこれを catch しておおむね `decryption_error` カテゴリで
 *   セッション全体を中止する。1 record の DEK アンラップ失敗でも安全側で
 *   全体停止 (= 個別 record の skip は対策 D の趣旨と矛盾する)。
 */
export async function unwrapDek(args: {
  wrappedDekCiphertextBase64: string;
  kek: CryptoKey;
}): Promise<Uint8Array> {
  const combined = decodeBase64(args.wrappedDekCiphertextBase64);
  if (combined.byteLength < NONCE_LENGTH + TAG_LENGTH_BYTES) {
    throw new CryptoError("wrapped_dek envelope shorter than nonce+tag");
  }
  const nonce = combined.subarray(0, NONCE_LENGTH);
  const ctAndTag = combined.subarray(NONCE_LENGTH);
  let dekBuf: ArrayBuffer;
  try {
    dekBuf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toFreshBuffer(nonce) },
      args.kek,
      toFreshBuffer(ctAndTag),
    );
  } catch {
    throw new CryptoError("DEK unwrap failed (KEK mismatch or tampered wrapped_dek)");
  }
  if (dekBuf.byteLength !== KEY_LENGTH_BYTES) {
    throw new CryptoError(`unexpected DEK length: ${dekBuf.byteLength}`);
  }
  return new Uint8Array(dekBuf);
}

/**
 * @what: Import DEK bytes into WebCrypto with `extractable: false` for use
 *   with `crypto.subtle.decrypt` against text fields and media bytes.
 * @why: KEK と同様に DEK の生バイトも JS から取り出せない状態で保持する。
 *   平文の DEK バイト列は本関数 import 直後に `zeroFill` で破棄する責務が
 *   呼び出し側にある (`exportRunner.ts` で実施)。
 * @failure: `CryptoError` を throw する条件:
 *   - dekBytes の長さが 32 byte 以外
 *   - WebCrypto の `importKey` 内部失敗。
 */
export async function importDek(dekBytes: Uint8Array): Promise<CryptoKey> {
  if (dekBytes.byteLength !== KEY_LENGTH_BYTES) {
    throw new CryptoError(`DEK must be ${KEY_LENGTH_BYTES} bytes, got ${dekBytes.byteLength}`);
  }
  return crypto.subtle.importKey("raw", toFreshBuffer(dekBytes), { name: "AES-GCM" }, false, [
    "decrypt",
  ]);
}

/**
 * @what: Decrypt a data envelope (`nonce(12) || ciphertext || tag(16)`) with
 *   a DEK CryptoKey, returning the plain bytes. Used for both UTF-8 text
 *   payloads and media binaries.
 * @why: Dart 側 `AesGcmEnvelope.encrypt` と同一レイアウトを採る。Web 側で
 *   1 record / 1 media ごとに本関数を呼び、復号済み平文を即座に zip stream に
 *   流すことでメモリ消費を 1 ファイル分に抑える。
 * @failure: `CryptoError` を throw する条件:
 *   - envelope が nonce(12) + tag(16) より短い
 *   - AES-GCM の auth tag が一致しない (DEK mismatch / 改ざん)。
 *   呼び出し側 (exportRunner) は 1 record の復号失敗でも全体を中止する
 *   (部分破損の zip を作らない方針)。
 */
export async function decryptDataEnvelope(args: {
  envelope: Uint8Array;
  dek: CryptoKey;
}): Promise<Uint8Array> {
  if (args.envelope.byteLength < NONCE_LENGTH + TAG_LENGTH_BYTES) {
    throw new CryptoError("data envelope shorter than nonce+tag");
  }
  const nonce = args.envelope.subarray(0, NONCE_LENGTH);
  const ctAndTag = args.envelope.subarray(NONCE_LENGTH);
  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toFreshBuffer(nonce) },
      args.dek,
      toFreshBuffer(ctAndTag),
    );
  } catch {
    throw new CryptoError("data decrypt failed (DEK mismatch or tampered ciphertext)");
  }
  return new Uint8Array(plaintext);
}

/**
 * @what: Decode a base64 text envelope and decrypt it to a UTF-8 string.
 * @why: `Entry.text` / `Observation.text` / `Digest.text` は Firestore 上では
 *   base64(envelope) で保存されている。data.json には平文文字列で書き出すため
 *   一発で復号する薄いラッパ。
 * @failure: 内部で [decryptDataEnvelope] を呼ぶため、その失敗条件をそのまま
 *   伝播する。base64 デコード失敗は `decodeBase64` が `DOMException` を投げる
 *   ため、呼び出し側でも最終的に `decryption_error` カテゴリで処理される。
 */
export async function decryptTextField(args: {
  ciphertextBase64: string;
  dek: CryptoKey;
}): Promise<string> {
  const envelope = decodeBase64(args.ciphertextBase64);
  const plaintext = await decryptDataEnvelope({ envelope, dek: args.dek });
  return new TextDecoder().decode(plaintext);
}
